import React from 'react';
import { t as tr } from '../i18n';
import type { Lang } from '../i18n';
import './ErrorBoundary.css';
import Icon from './Icon';

// 最后一道网。没有它,子树里任何一个未捕获的异常都会把**整棵 React 树卸载掉** ——
// 网页上剩一个空 body,Electron 上就是**白屏**,而且用户不知道发生了什么。
//
// ★ 为什么必须共享
//   桌面**根本没有** error boundary(审计:grep 零命中)。而我今天刚好亲手证明了
//   它多需要:一个 TDZ 错误(在声明之前用了 const)就让整个 Studio 白屏。
//   网页那份只依赖 react + i18n,零平台代码 —— 本来就该住在共享层。
//
// 只捕获渲染/生命周期错误。事件处理里的异步异常走 window.onerror,不归它管。

function useDetect(): Lang {
  // class component 用不了 hook —— 语言只能命令式地读一次。
  // 两端的键是同一个(al_lang),因为语言状态本来就该只有一份。
  try {
    return (localStorage.getItem('al_lang') === 'en' ? 'en' : 'zh') as Lang;
  } catch {
    return 'zh';
  }
}

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode; label?: string },
  { err: Error | null }
> {
  state: { err: Error | null } = { err: null };

  static getDerivedStateFromError(err: Error) { return { err }; }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label ?? '(root)', err, info);
  }

  render() {
    if (!this.state.err) return this.props.children;
    const t = (k: string) => tr(k, useDetect());
    return (
      <div className="error-boundary">
        <h2><Icon name="warning" size={18} /> {t('error_boundary.heading')}</h2>
        <p>
          {this.props.label && <span>{t('error_boundary.location')}<code>{this.props.label}</code><br /></span>}
          {t('error_boundary.error')}<code>{this.state.err.message}</code>
        </p>
        <details>
          <summary>{t('error_boundary.stack')}</summary>
          <pre>{this.state.err.stack}</pre>
        </details>
        <div className="error-boundary-actions">
          <button className="btn btn-primary" onClick={() => this.setState({ err: null })}>
            {t('error_boundary.retry')}
          </button>
          <button className="btn btn-ghost" onClick={() => window.location.reload()}>
            {t('error_boundary.refresh')}
          </button>
        </div>
      </div>
    );
  }
}
